import { Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { DeleteAssessmentDialog } from "@/components/delete-assessment-dialog";
import { Eyebrow } from "@/components/eyebrow";
import { MarkdownArticle } from "@/components/markdown-article";
import { PageShell } from "@/components/page-shell";
import { Scorecard } from "@/components/scorecard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DownloadReportButton } from "@/features/report/download-button";
import { InvitePanel } from "@/features/report/invite-panel";
import { SharePanel } from "@/features/report/share-panel";
import { type AssessmentRecord, useAssessment } from "@/lib/api";

export function ReportPage() {
  const { id } = useParams<{ id: string }>();
  const query = useAssessment(id ?? null);

  const status = (query.error as { status?: number } | null)?.status;
  const returnTo = id ? `/report/${id}` : "/history";

  return (
    <PageShell>
      {query.isLoading && (
        <div className="flex justify-center py-24">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </div>
      )}
      {query.error != null &&
        (status === 401 ? (
          <Card className="mx-auto max-w-md">
            <CardContent className="space-y-5 pt-6">
              <div className="text-center">
                <Eyebrow>Sign in required</Eyebrow>
                <h1 className="text-primary mt-3 text-2xl font-normal tracking-tight">
                  Sign in to view this report.
                </h1>
                <p className="text-muted-foreground mt-3 text-sm">
                  Reports are private to the account that created them. Sign in to open it.
                </p>
              </div>
              <Button asChild variant="outline" className="w-full">
                <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`}>
                  Sign in with email
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : status === 403 ? (
          <div className="py-12 text-center">
            <Eyebrow>Private report</Eyebrow>
            <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
              This report belongs to a different account.
            </h1>
            <p className="text-muted-foreground mt-4">
              Reports are private to the account that created them. Check your own history, or sign
              in with the account that ran this assessment.
            </p>
            <Button asChild className="mt-8">
              <Link to="/history">Go to your history</Link>
            </Button>
          </div>
        ) : status === 404 ? (
          <div className="py-12 text-center">
            <Eyebrow>404</Eyebrow>
            <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
              Not found.
            </h1>
            <p className="text-muted-foreground mt-4">That assessment or page doesn’t exist.</p>
            <Button asChild className="mt-8">
              <Link to="/assess">Start an assessment</Link>
            </Button>
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Couldn’t load the report. Please try again.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ))}
      {query.data && <ReportBody record={query.data} />}
    </PageShell>
  );
}

interface ReportBodyProps {
  record: AssessmentRecord;
}

function ReportBody({ record }: ReportBodyProps) {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { result, assessment, effective_scores } = record;
  const reviewed = record.status === "reviewed" && effective_scores != null;

  // Expert calibration adjusts the layer scores and position, but overall
  // exposure, confidence, and exposure level always come from the original
  // system result.
  const display =
    reviewed && effective_scores
      ? {
          businessModel: effective_scores.business_model_disruption_score,
          operatingModel: effective_scores.operating_model_disruption_score,
          protector: effective_scores.structural_protector_score,
          strategicPosition: effective_scores.strategic_position,
        }
      : {
          businessModel: result.business_model_disruption_score,
          operatingModel: result.operating_model_disruption_score,
          protector: result.structural_protector_score,
          strategicPosition: result.strategic_position,
        };

  return (
    <div>
      <p className="mb-3 font-mono text-xs print:hidden">
        <Link to="/" className="text-accent hover:underline">
          ← Home
        </Link>
      </p>
      <Eyebrow>AI Disruption Exposure Report</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        {assessment.company_name}.
      </h1>
      <p className="text-muted mt-2">
        {assessment.sector}
        {assessment.geography ? ` · ${assessment.geography}` : ""} · generated{" "}
        {new Date(record.created_at).toLocaleDateString()}
      </p>

      {reviewed && (
        <div className="border-accent bg-accent-light/30 text-primary mt-6 rounded-md border px-4 py-3 text-sm">
          Reviewed and calibrated by a BOI expert. The scorecard reflects the expert-adjusted view;
          the original system scores are preserved.
        </div>
      )}

      {/* Owner-only controls. The backend embeds `share` only for the report's
          owner, so this gate also keeps them off the anonymous shared page and
          away from an admin viewing someone else's report. `print:hidden` drops
          them from the downloaded copy. */}
      {record.share != null && (
        <div className="print:hidden">
          <SharePanel assessmentId={record.assessment_id} share={record.share} />
          <InvitePanel assessmentId={record.assessment_id} />
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-4 w-4" />
              Delete report
            </Button>
            <DownloadReportButton companyName={assessment.company_name} />
          </div>
        </div>
      )}

      <DeleteAssessmentDialog
        assessmentId={record.assessment_id}
        companyName={assessment.company_name}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={() => navigate("/history")}
      />

      <Card className="mt-6">
        <CardContent className="pt-6">
          <Scorecard
            businessModel={display.businessModel}
            operatingModel={display.operatingModel}
            protector={display.protector}
            overall={result.overall_exposure_score}
            confidence={result.confidence_level}
            exposureLevel={result.exposure_level}
            strategicPosition={display.strategicPosition}
          />
        </CardContent>
      </Card>

      <Card className="mt-5">
        <CardContent className="pt-6">
          <MarkdownArticle markdown={record.report_markdown} />
        </CardContent>
      </Card>

      <p className="text-muted mt-6 font-mono text-xs">Reference: {record.assessment_id}</p>
    </div>
  );
}
